import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/vcDialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Video, Mic } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

export type HostRequestType = "video" | "audio"

interface HostRequestDialogProps {
  open: boolean
  type: HostRequestType
  hostName?: string
  onAccept: () => void
  onDecline: () => void
}

export function HostRequestDialog({
  open,
  type,
  hostName = "Host",
  onAccept,
  onDecline,
}: HostRequestDialogProps) {
  const t = useTranslation();
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    if (!open) return

    setProgress(100)

    const duration = 30000
    const interval = 50
    const decrement = (interval / duration) * 100

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev - decrement
        if (next <= 0) {
          clearInterval(timer)
          onDecline()
          return 0
        }
        return next
      })
    }, interval)

    return () => clearInterval(timer)
  }, [open, onDecline])

  const isVideo = type === "video"
  const Icon = isVideo ? Video : Mic

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onDecline()}>
      <DialogContent maxWidthClass="sm:max-w-md" className="p-0 gap-0 overflow-hidden bg-card">
        {/* <button
          onClick={onDecline}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button> */}

        <DialogHeader className="space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 ring-4 ring-blue-500/10 animate-[pulse_2s_ease-in-out_infinite]">
            <Icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <DialogTitle className="text-gray-900 dark:text-foreground text-center text-xl">
            {isVideo ? t("dialogs.enableYourCamera") : t("dialogs.enableYourMicrophone")}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            <span className="font-medium text-foreground">{hostName}</span> {t("dialogs.hasRequestedTurnOn")}{" "}
            {isVideo ? t("dialogs.camera") : t("dialogs.microphoneLower")}.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-1.5 my-4 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full bg-primary transition-all duration-100 ease-linear",
              progress < 30 && "from-amber-500 to-orange-500",
              progress < 10 && "from-red-500 to-rose-500"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={onDecline}
            className="flex-1 sm:flex-none"
          >
            {t("dialogs.decline")}
          </Button>
          <Button
            variant={'default'}
            onClick={onAccept}
          >
            {t("dialogs.enable")} {isVideo ? t("dialogs.camera") : t("dialogs.microphoneLower")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
